import { useUsdPeggedCurrencies } from '@src/containers/Blotters/OTC/otcConstants';
import {
  USD_PEGGED_NO_CONVERSION_INDEX,
  usePriceSourceFixingsByFullSymbol,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import type { PayoffTypeEnum } from '@talos/kyoko';
import { createCustomSortFilter } from '@talos/kyoko';
import { useMemo } from 'react';
import { getNotionalCurrencies } from '../../OMS/ManualDealTradeView/utils';
import { FORWARD_PAYOFF_TYPE_OPTIONS } from '../productDefinitions/tokens';
import type { OTCFieldsContext } from './types';

export type UsdPeggedOption = { value: string; label: string; description: string };
export type PayoffTypeOption = { value: PayoffTypeEnum; label: PayoffTypeEnum };

interface CommonProductFormData {
  payoffType?: PayoffTypeEnum;
  quoteAsset?: string;
  deliveryAsset?: string;
  index?: string;
}

export type IndexOption = { value: string; label: string; description: string };

interface UseCommonOtcDerivativeProductLayoutLogicResult {
  usdPeggedOptions: UsdPeggedOption[];
  usdPeggedSortFilter: ReturnType<typeof createCustomSortFilter<UsdPeggedOption>>;
  availableIndexes: IndexOption[];
  selectedIndex: IndexOption | undefined;
  payoffDependentData: {
    unitNotionalCurrency: string | undefined;
    quoteAssetPlaceholder: string;
    deliveryAssetPlaceholder: string;
  };
  assetSelections: {
    quoteAssetSelection: UsdPeggedOption | undefined;
    deliveryAssetSelection: UsdPeggedOption | undefined;
  };
  payoffTypeSelection: PayoffTypeOption | undefined;
}

/**
 * Shared hook for common logic between Forward and Option OTC derivative product layouts.
 * Handles USD pegged currencies, settlement index, payoff-dependent calculations,
 * and asset selections.
 */
export function useCommonOtcDerivativeProductLayoutLogic<T extends CommonProductFormData>(
  formData: T,
  disclosureContext: OTCFieldsContext
): UseCommonOtcDerivativeProductLayoutLogicResult {
  const { baseAsset } = disclosureContext.rowData;
  const isOptionProduct = disclosureContext.rowData.product === 'option';

  const usdPeggedCurrencies = useUsdPeggedCurrencies();
  const usdPeggedOptions = useMemo(() => {
    return usdPeggedCurrencies.map(currency => ({
      value: currency.Symbol,
      label: currency.Symbol,
      description: currency.Description,
    }));
  }, [usdPeggedCurrencies]);

  const normalizedQuoteAssetForFixings = useMemo(() => {
    if (formData.payoffType === 'Inverse' && isOptionProduct) {
      return 'USD';
    }
    return formData.quoteAsset === 'USDT' || formData.quoteAsset === 'USDC' ? 'USD' : formData.quoteAsset;
  }, [formData.payoffType, formData.quoteAsset, isOptionProduct]);

  const fixings = usePriceSourceFixingsByFullSymbol(baseAsset ?? undefined, normalizedQuoteAssetForFixings);

  const availableIndexes = useMemo(() => {
    if (!baseAsset) {
      return [];
    }
    // Inverse options: quote is base; fixings list still uses (base, USD)
    if (!(formData.payoffType === 'Inverse' && isOptionProduct) && !formData.quoteAsset) {
      return [];
    }

    return fixings
      .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
      .map(fixing => ({
        value: fixing.Name,
        label: fixing.DisplayName ?? fixing.Name,
        description: fixing.Description ?? '',
      }));
  }, [baseAsset, formData.quoteAsset, formData.payoffType, isOptionProduct, fixings]);

  const selectedIndex = useMemo(() => {
    if (!formData.index) {
      return undefined;
    }
    return availableIndexes.find(option => option.value === formData.index);
  }, [formData.index, availableIndexes]);

  const payoffDependentData = useMemo(() => {
    const { unitNotionalCurrency } = getNotionalCurrencies(formData.payoffType, baseAsset, formData.quoteAsset);

    let quoteAssetPlaceholder = 'Select Quote Asset';
    let deliveryAssetPlaceholder = '';
    switch (formData.payoffType) {
      case 'Inverse':
        deliveryAssetPlaceholder = `${baseAsset} (underlying)`;
        if (isOptionProduct) {
          quoteAssetPlaceholder = `${baseAsset} (underlying)`;
        }
        break;
      case 'Linear':
        deliveryAssetPlaceholder = formData.quoteAsset ? `${formData.quoteAsset} (Quote Asset)` : 'Select Quote Asset';
        break;
    }

    return {
      unitNotionalCurrency,
      quoteAssetPlaceholder,
      deliveryAssetPlaceholder,
    };
  }, [formData.payoffType, baseAsset, formData.quoteAsset, isOptionProduct]);

  const usdPeggedSortFilter = useMemo(() => createCustomSortFilter<UsdPeggedOption>(), []);

  const assetSelections = useMemo(() => {
    const quoteFromOptions = usdPeggedOptions.find(option => option.value === formData.quoteAsset);
    const quoteAssetSelection =
      quoteFromOptions ??
      (formData.quoteAsset ? { value: formData.quoteAsset, label: formData.quoteAsset, description: '' } : undefined);
    const deliveryAssetSelection =
      formData.payoffType === 'Linear'
        ? usdPeggedOptions.find(option => option.value === formData.deliveryAsset)
        : undefined;

    return {
      quoteAssetSelection,
      deliveryAssetSelection,
    };
  }, [usdPeggedOptions, formData.quoteAsset, formData.payoffType, formData.deliveryAsset]);

  const payoffTypeSelection = useMemo(() => {
    return FORWARD_PAYOFF_TYPE_OPTIONS.find(option => option.value === formData.payoffType);
  }, [formData.payoffType]);

  return {
    usdPeggedOptions,
    usdPeggedSortFilter,
    availableIndexes,
    selectedIndex,
    payoffDependentData,
    assetSelections,
    payoffTypeSelection,
  };
}
